import { useMemo, useState } from "react";

import dayjs from "dayjs";
import { CalendarClock, Download, Plus, Search, Upload } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import {
  useCreateSupplier,
  useDeleteSupplier,
  useSupplierBalancesSummaryAtCutoff,
  useSupplierDocumentsAtCutoff,
  useSuppliers,
  useSuppliersWithMovements,
  useToggleSupplierActive,
  useUpdateSupplier,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { formatCurrency } from "~/utils/helpers";

import { SupplierBalanceUploadDialog } from "./components/SupplierBalanceUploadDialog";
import { SupplierDialog } from "./components/SupplierDialog";
import { SuppliersTable } from "./components/SuppliersTable";
import {
  exportSupplierAgingConsolidado,
  exportSupplierAgingPorCodigo,
} from "./utils/exportSupplierAgingReports";
import { exportSuppliersToExcel } from "./utils/exportSuppliers";

import type { Supplier, SupplierFormData } from "~/types/purchasing";

export function SuppliersPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [balanceUploadOpen, setBalanceUploadOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | undefined>(
    undefined
  );
  const [deleteSupplier, setDeleteSupplier] = useState<Supplier | undefined>(
    undefined
  );
  const [searchTerm, setSearchTerm] = useState("");
  // Corte histórico: null = saldo actual (hoy); a date = reconstruct AP at that month-end.
  const today = dayjs().format("YYYY-MM-DD");
  const [cutoff, setCutoff] = useState<string | null>(null);
  const effectiveCutoff = cutoff ?? today;

  const { toast } = useToast();

  const { data: suppliers = [], isLoading } = useSuppliers(
    searchTerm ? { search: searchTerm } : undefined
  );
  const { data: balanceSummary = [] } =
    useSupplierBalancesSummaryAtCutoff(effectiveCutoff);
  const { data: docsAtCutoff = [] } = useSupplierDocumentsAtCutoff(
    effectiveCutoff,
    true
  );
  const { data: movementSupplierIds } = useSuppliersWithMovements();
  const balances = useMemo(
    () => new Map(balanceSummary.map((b) => [b.supplier_id, b])),
    [balanceSummary]
  );
  // When a search is active, the aging exports must mirror the filtered table: keep only the
  // documents whose proveedor is in the currently-matched `suppliers` set (search is server-side
  // name/RUT ilike, so reusing those ids keeps every export consistent).
  const visibleDocs = useMemo(() => {
    if (!searchTerm) return docsAtCutoff;
    const matchedIds = new Set(suppliers.map((s) => s.id));
    return docsAtCutoff.filter((d) => matchedIds.has(d.supplier_id));
  }, [docsAtCutoff, suppliers, searchTerm]);
  const totals = useMemo(
    () =>
      balanceSummary.reduce(
        (acc, b) => ({
          total: acc.total + Number(b.saldo_total ?? 0),
          overdue: acc.overdue + Number(b.overdue_amount ?? 0),
          docs: acc.docs + Number(b.open_docs_count ?? 0),
        }),
        { total: 0, overdue: 0, docs: 0 }
      ),
    [balanceSummary]
  );
  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier();
  const deleteMutation = useDeleteSupplier();
  const toggleActiveMutation = useToggleSupplierActive();

  const handleCreate = () => {
    setEditingSupplier(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setDialogOpen(true);
  };

  const handleSubmit = (data: SupplierFormData) => {
    if (editingSupplier) {
      updateMutation.mutate(
        { id: editingSupplier.id, data },
        {
          onSuccess: () => {
            toast({
              title: "Proveedor actualizado",
              description: `${data.name} ha sido actualizado`,
            });
            setDialogOpen(false);
            setEditingSupplier(undefined);
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          toast({
            title: "Proveedor creado",
            description: `${data.name} ha sido creado`,
          });
          setDialogOpen(false);
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        },
      });
    }
  };

  const handleDelete = (supplier: Supplier) => {
    setDeleteSupplier(supplier);
  };

  const confirmDelete = () => {
    if (deleteSupplier) {
      deleteMutation.mutate(deleteSupplier.id, {
        onSuccess: () => {
          toast({
            title: "Proveedor eliminado",
            description: `${deleteSupplier.name} ha sido eliminado`,
          });
          setDeleteSupplier(undefined);
        },
        onError: (error) => {
          toast({
            title: "No se pudo eliminar",
            description: error.message,
            variant: "destructive",
          });
          setDeleteSupplier(undefined);
        },
      });
    }
  };

  const handleToggleActive = (supplier: Supplier) => {
    toggleActiveMutation.mutate({
      id: supplier.id,
      is_active: !supplier.is_active,
    });
  };

  const handleExportSuppliers = () => {
    exportSuppliersToExcel(suppliers, balances);
    toast({
      title: "Exportando proveedores",
      description: `${suppliers.length} proveedores exportados a Excel`,
    });
  };

  const cutoffLabel = dayjs(effectiveCutoff).format("DD/MM/YYYY");

  const handleExportConsolidado = () => {
    exportSupplierAgingConsolidado(visibleDocs, effectiveCutoff);
    toast({
      title: "Exportando reporte consolidado",
      description: `${visibleDocs.length} documentos al ${cutoffLabel}`,
    });
  };

  const handleExportPorCodigo = () => {
    exportSupplierAgingPorCodigo(visibleDocs, effectiveCutoff);
    toast({
      title: "Exportando reporte por proveedor",
      description: `${visibleDocs.length} documentos al ${cutoffLabel}`,
    });
  };

  return (
    <PageContainer>
      <PageHeader
        title="Proveedores"
        description="Gestión de proveedores"
        actions={
          <>
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                aria-label="Fecha de corte histórica"
                value={effectiveCutoff}
                max={today}
                onChange={(e) =>
                  setCutoff(e.target.value === today ? null : e.target.value)
                }
                className="h-9 w-40"
              />
              {cutoff && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9"
                  onClick={() => setCutoff(null)}
                >
                  Hoy
                </Button>
              )}
            </div>
            <Button variant="outline" onClick={() => setBalanceUploadOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Cargar saldos
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={suppliers.length === 0}>
                  <Download className="mr-2 h-4 w-4" />
                  Exportar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Análisis de vencimiento</DropdownMenuLabel>
                <DropdownMenuItem onClick={handleExportConsolidado}>
                  Consolidado (línea por línea)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportPorCodigo}>
                  Por proveedor (con subtotales)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleExportSuppliers}>
                  Lista de proveedores
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Proveedor
            </Button>
          </>
        }
      />

      {balanceSummary.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total por pagar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                ${formatCurrency(totals.total)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {cutoff ? "Saldo histórico al" : "Saldo actual al"} {cutoffLabel}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Vencido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-700">
                ${formatCurrency(totals.overdue)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Al {cutoffLabel}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Proveedores con saldo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{balanceSummary.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Documentos abiertos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{totals.docs}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-sm font-medium">
              Lista de Proveedores
              <span className="ml-2 font-normal text-muted-foreground">
                ({suppliers.length})
              </span>
            </CardTitle>
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o RUT..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 w-full pl-8 sm:w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <SuppliersTable
            suppliers={suppliers}
            isLoading={isLoading}
            balances={balances}
            movementSupplierIds={movementSupplierIds}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
          />
        </CardContent>
      </Card>

      <SupplierDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        supplier={editingSupplier}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <SupplierBalanceUploadDialog
        open={balanceUploadOpen}
        onOpenChange={setBalanceUploadOpen}
      />

      <AlertDialog
        open={!!deleteSupplier}
        onOpenChange={(open) => !open && setDeleteSupplier(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Proveedor</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea eliminar el proveedor "
              {deleteSupplier?.name}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
